import { computed } from "@nuxtjs/composition-api";
import { sharedRef, useVSFContext, Logger } from "@vue-storefront/core";
import { coveoGetters } from "~/composables/getters";
import { COOKIES } from "~/helpers/constants";

interface ErrorState {
  auth: Error | null;
  api: Error | null;
}

const useCoveo = (id: string) => {
  const context = useVSFContext();
  const result = sharedRef<any>(null, `auth-${id}`);
  const loading = sharedRef<boolean>(false, `auth-loading-${id}`);
  const error = sharedRef<ErrorState>(
    { auth: null, api: null },
    `auth-error-${id}`
  );
  const coveoToken = sharedRef<string>("", COOKIES.COVEO_TOKEN);

  const load = async (params: any) => {
    Logger.debug(`useCoveo/${id}/load`, params);
    try {
      loading.value = true;

      const existingCoveoToken = context.$cookies.get(COOKIES.COVEO_TOKEN);
      if (existingCoveoToken) {
        result.value = existingCoveoToken;
      } else {
        result.value = await context.$vsf.$coveo.api.auth(params);

        const newCoveoToken = coveoGetters.getCoveoToken(result.value);
        context.$cookies.set(COOKIES.COVEO_TOKEN, newCoveoToken, {
          maxAge: 60 * 60 * 24 * 1,
          path: "/",
          secure: true,
          httpOnly: true,
        });
        coveoToken.value = newCoveoToken;
      }
    } catch (err) {
      error.value.auth = err;
      Logger.error(`Auth Error ${error.value.auth}`);
    } finally {
      loading.value = false;
    }
    return result;
  };

  const search = async (params: any) => {
    Logger.debug(`useCoveo/${id}/search`, params);
    const searchHub = `${context.i18n.localeProperties.coveoSearchHub}_${context.i18n.localeProperties.languageCode}_LISTING`;
    try {
      params.token =
        params?.token ||
        context.$cookies.get(COOKIES.COVEO_TOKEN) ||
        coveoToken.value;
      params.searchHub = params?.searchHub || searchHub;
      params.country = context.i18n.localeProperties.baseStoreCode;
      loading.value = true;
      if (!params.token) {
        await load({
          searchHub,
          locale: context.i18n.localeProperties.baseStoreCode,
        });
        params.token = coveoToken.value;
      }

      result.value = await context.$vsf.$coveo.api.search(params);
    } catch (err) {
      error.value.api = err;
      Logger.error(`Error ${error.value.api}`);
    } finally {
      loading.value = false;
    }
    return result;
  };

  return {
    load,
    search,
    result: computed(() => result.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  };
};

export default useCoveo;
